import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restaurants, setRestaurants] = useState([]);

  // API Base URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Check if user is logged in on app start
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        // Verify token with backend
        
        const response = await fetch(`${API_BASE_URL}/api/auth/verify`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const userData = await response.json();
          console.log('Auth check - user data:', userData);
          setUser(userData);
          await loadUserRestaurants(userData);
        } else {
          // Token is invalid, remove it
          console.log('Auth check failed:', response.status, response.statusText);
          localStorage.removeItem('authToken');
          setUser(null);
        }
      }
    } catch (error) {
      console.error('Auth check error:', error);
      localStorage.removeItem('authToken');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const loadUserRestaurants = async (userData) => {
    try {
      const token = localStorage.getItem('authToken');
      
      console.log('Loading restaurants for user:', userData);
      
      // Convert numeric role to string for comparison
      const userRole = typeof userData.role === 'number' ? 
        (userData.role === 1 ? 'Admin' : userData.role === 2 ? 'Manager' : userData.role === 3 ? 'SuperAdmin' : 'User') : 
        userData.role;
      
      // Use role-based endpoint first
      try {
        const roleBasedUrl = `${API_BASE_URL}/api/restaurants/by-role`;
        console.log('Trying role-based endpoint:', roleBasedUrl);
        
        const roleResponse = await fetch(roleBasedUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (roleResponse.ok) {
          const roleRestaurants = await roleResponse.json();
          console.log('Restaurants loaded from role-based endpoint:', roleRestaurants);
          setRestaurants(roleRestaurants);
          return;
        } else {
          console.log('Role-based endpoint failed, falling back to user-specific endpoint');
        }
      } catch (error) {
        console.log('Role-based endpoint error, falling back to user-specific endpoint');
      }

      // Fallback to user-specific endpoint
      if (userData.id) {
        try {
          const userSpecificUrl = `${API_BASE_URL}/api/restaurants/user/${userData.id}`;
          console.log('Trying user-specific endpoint:', userSpecificUrl);
          
          const userResponse = await fetch(userSpecificUrl, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (userResponse.ok) {
            const userRestaurants = await userResponse.json();
            console.log('Restaurants loaded from user endpoint:', userRestaurants);
            setRestaurants(userRestaurants);
            return;
          } else {
            console.log('User-specific endpoint failed, falling back to all restaurants');
          }
        } catch (error) {
          console.log('User-specific endpoint error, falling back to all restaurants');
        }
      }

      // Final fallback to all restaurants
      const url = `${API_BASE_URL}/api/restaurants`;
      console.log('Loading all restaurants from:', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const restaurantsData = await response.json();
        console.log('All restaurants loaded:', restaurantsData);
        
        // If user is not SuperAdmin, filter by their restaurantId
        if (userRole !== 'SuperAdmin' && userData.restaurantId) {
          const filteredRestaurants = restaurantsData.filter(restaurant => 
            restaurant.id === userData.restaurantId || 
            restaurant.restaurantId === userData.restaurantId
          );
          console.log('Filtered restaurants for user:', filteredRestaurants);
          setRestaurants(filteredRestaurants);
        } else {
          console.log('Setting all restaurants for SuperAdmin');
          setRestaurants(restaurantsData);
        }
      } else {
        console.error('Failed to load restaurants:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error loading user restaurants:', error);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Giriş başarısız');
      }

      const data = await response.json();
      
      console.log('Login response:', data);
      
      // Store token
      localStorage.setItem('authToken', data.token);
      
      // Set user data
      setUser(data.user);
      
      // Load user's restaurants
      await loadUserRestaurants(data.user);
      
      return { success: true, user: data.user };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    setUser(null);
    setRestaurants([]);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    
    // Convert numeric role to string
    const userRole = typeof user.role === 'number' ? 
      (user.role === 1 ? 'Admin' : user.role === 2 ? 'Manager' : user.role === 3 ? 'SuperAdmin' : 'User') : 
      user.role;
    
    switch (permission) {
      case 'manage_all_restaurants':
        return userRole === 'SuperAdmin';
      case 'create_restaurant':
        // Yeni restoran yalnızca SuperAdmin ekleyebilir (Admin'ler ekleyemez).
        return userRole === 'SuperAdmin';
      case 'delete_restaurant':
        return userRole === 'SuperAdmin';
      case 'manage_own_restaurant':
        return ['SuperAdmin', 'Admin'].includes(userRole);
      case 'manage_products':
        return ['SuperAdmin', 'Admin', 'Manager'].includes(userRole);
      case 'view_reports':
        return ['SuperAdmin', 'Admin'].includes(userRole);
      case 'manage_users':
        return ['SuperAdmin', 'Admin'].includes(userRole);
      default:
        return false;
    }
  };

  const canAccessRestaurant = (restaurantId) => {
    if (!user) return false;
    
    // Convert numeric role to string
    const userRole = typeof user.role === 'number' ? 
      (user.role === 1 ? 'Admin' : user.role === 2 ? 'Manager' : user.role === 3 ? 'SuperAdmin' : 'User') : 
      user.role;
    
    if (userRole === 'SuperAdmin') return true;
    return user.restaurantId === restaurantId;
  };

  const value = {
    user,
    restaurants,
    loading,
    login,
    logout,
    updateUser,
    hasPermission,
    canAccessRestaurant,
    loadUserRestaurants
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
